import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import TwoFactorModal from './components/TwoFactorModal';
import TestimonialCarousel from './components/TestimonialCarousel';

const LoginRegister = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionSecure, setConnectionSecure] = useState(true);

  const securityFeatures = [
    { icon: 'Shield', label: 'Bank-level Security' },
    { icon: 'Lock', label: '256-bit SSL Encryption' },
    { icon: 'CheckCircle', label: 'SOC 2 Compliant' },
    { icon: 'Eye', label: 'Advanced Monitoring' }
  ];

  useEffect(() => {
    // Simulate connection security check
    const timer = setTimeout(() => {
      setConnectionSecure(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (credentials) => {
    setIsLoading(true);
    
    // Mock authentication with predefined credentials
    const mockCredentials = {
      email: 'trader@cryptotrade.com',
      password: 'SecurePass123!'
    };

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (credentials.email === mockCredentials.email && credentials.password === mockCredentials.password) {
        setShowTwoFactor(true);
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      alert('Invalid credentials. Please use: trader@cryptotrade.com / SecurePass123!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (userData) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Registration successful! Please check your email for verification.');
      setActiveTab('login');
    } catch (error) {
      alert('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSuccess = () => {
    setShowTwoFactor(false);
    navigate('/trading-dashboard');
  };

  const handleSocialLogin = (provider) => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      alert(`${provider} login would redirect to ${provider} authentication`);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="flex min-h-screen">
        {/* Left Panel - Branding & Testimonials */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary-700 relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10"></div>
          
          <div className="relative z-10 flex flex-col justify-between p-12 text-white">
            {/* Logo & Branding */}
            <div>
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <Icon name="TrendingUp" size={28} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading-bold">CryptoTrade Pro</h1>
                  <p className="text-primary-100 text-sm">Professional Trading Platform</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-4xl font-heading-bold mb-4 leading-tight">
                  Trade with Confidence
                </h2>
                <p className="text-primary-100 text-lg leading-relaxed">
                  Join thousands of professional traders who trust our platform for secure, 
                  fast, and reliable cryptocurrency trading.
                </p>
              </div>

              {/* Security Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {securityFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                      <Icon name={feature.icon} size={16} color="white" />
                    </div>
                    <span className="text-sm font-body-medium">{feature.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <TestimonialCarousel />

            {/* Connection Status */}
            <div className="flex items-center space-x-2 mt-8">
              <div className={`w-2 h-2 rounded-full ${connectionSecure ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`}></div>
              <span className="text-sm text-primary-100">
                {connectionSecure ? 'Secure Connection Established' : 'Establishing Secure Connection...'}
              </span>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-white bg-opacity-10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full blur-xl"></div>
        </div>

        {/* Right Panel - Authentication Forms */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center space-x-3 mb-8">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={24} color="white" />
              </div>
              <span className="text-xl font-heading-bold text-text-primary">CryptoTrade Pro</span>
            </div>

            {/* Tab Navigation */}
            <div className="flex bg-secondary-50 rounded-trading p-1 mb-8">
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 py-3 px-4 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                  activeTab === 'login' ?'bg-surface text-primary shadow-trading-card' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setActiveTab('register')}
                className={`flex-1 py-3 px-4 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                  activeTab === 'register' ?'bg-surface text-primary shadow-trading-card' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                Create Account
              </button>
            </div>

            {/* Forms */}
            {activeTab === 'login' ? (
              <LoginForm
                onSubmit={handleLogin}
                onForgotPassword={() => setShowForgotPassword(true)}
                isLoading={isLoading}
              />
            ) : (
              <RegisterForm
                onSubmit={handleRegister}
                isLoading={isLoading}
              />
            )}

            {/* Social Login */}
            <div className="mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-background text-text-secondary font-body-normal">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSocialLogin('Google')}
                  disabled={isLoading}
                  className="flex items-center justify-center px-4 py-3 border border-border rounded-trading bg-surface hover:bg-secondary-50 transition-colors duration-trading-fast disabled:opacity-50"
                >
                  <Icon name="Chrome" size={20} className="text-text-secondary" />
                  <span className="ml-2 font-body-medium text-sm text-text-primary">Google</span>
                </button>
                <button
                  onClick={() => handleSocialLogin('Apple')}
                  disabled={isLoading}
                  className="flex items-center justify-center px-4 py-3 border border-border rounded-trading bg-surface hover:bg-secondary-50 transition-colors duration-trading-fast disabled:opacity-50"
                >
                  <Icon name="Apple" size={20} className="text-text-secondary" />
                  <span className="ml-2 font-body-medium text-sm text-text-primary">Apple</span>
                </button>
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-8 text-center">
              <p className="text-xs text-text-secondary">
                By continuing, you agree to our{' '}
                <button className="text-primary hover:text-primary-600 font-body-medium">
                  Terms of Service
                </button>{' '}
                and{' '}
                <button className="text-primary hover:text-primary-600 font-body-medium">
                  Privacy Policy
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
        />
      )}

      {showTwoFactor && (
        <TwoFactorModal
          onSuccess={handleTwoFactorSuccess}
          onClose={() => setShowTwoFactor(false)}
        />
      )}
    </div>
  );
};

export default LoginRegister;